import * as React from 'react'
import { CircularProgress, Container } from '@applift/factor'

import { DEFAULT_CREATIVE_LIST_SORTING } from '../../constants/creative'
import { ModelledCreativeGridWrapper } from '../../pages/CreativePage/component/ModelledCreativeGrid/ModelledCreativeGridWrapper'
import {
  useCampaignDetails,
  useDimensions,
  useModelledCreativesList,
} from '../../hooks'
import { CreativeModelledListResponse } from '../../models/Creative'
import {
  CAMPAIGN_BUDGET_TYPE,
  CAMPAIGN_STATUSES,
} from '../../constants/campaigns'

export interface ViewModelledCreativesProps {
  campaignId: number
  campaignStatus: string
  currentApp: 'campaign' | 'customers'
}

export const ViewModelledCreatives = (props: ViewModelledCreativesProps) => {
  const { campaignId, campaignStatus, currentApp } = props

  const { data: dimensionData, isLoading: isDimensionBeingFetched } =
    useDimensions()

  const dimensionId = React.useMemo(() => {
    if (dimensionData) {
      return dimensionData.keyIdPair['creative']
    }
  }, [dimensionData])

  const { data: campaignDetails } = useCampaignDetails({
    id: Number(campaignId),
  })

  const campaignBudget = React.useMemo(
    () =>
      campaignDetails?.budgetTypeId === CAMPAIGN_BUDGET_TYPE.DOLLAR
        ? campaignDetails.budgetTotal
        : campaignDetails?.totalImpressions,
    [campaignDetails]
  )

  const {
    data: modelledCreativesData,
    isFetching: isModelledCreativesBeingFetched,
  } = useModelledCreativesList(
    {
      skipReporting: [
        CAMPAIGN_STATUSES.DRAFT,
        CAMPAIGN_STATUSES.PENDING,
      ].includes(campaignStatus),
      dimensionId: dimensionId as number,
      campaignId: campaignId as number,
      sorting: DEFAULT_CREATIVE_LIST_SORTING,
      pageNo: -1,
    },
    {
      enabled: Boolean(dimensionId) && typeof campaignBudget === 'number',
      meta: { campaignBudget },
    }
  )

  const columnsToDisplay = [
    'id',
    'creativeThumbnailSource',
    'name',
    'creativeStatus',
    'priority',
    'spendRatio',
    'spendRatioTypeId',
    'bidMultiplier',
  ]

  return isDimensionBeingFetched || isModelledCreativesBeingFetched ? (
    <Container
      sx={{
        height: 100,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        my: 'auto',
      }}
    >
      <CircularProgress size={64} thickness={2} />
    </Container>
  ) : (
    <ModelledCreativeGridWrapper
      campaignId={campaignId}
      modifiedModelledData={
        modelledCreativesData as CreativeModelledListResponse
      }
      isFetching={isModelledCreativesBeingFetched}
      dimensionId={dimensionId as number}
      isViewOnly
      columnsToDisplay={columnsToDisplay}
      campaignStatus={campaignStatus}
      currentApp={currentApp}
    />
  )
}
